import axios from 'axios';
import { useInterfaceStore } from '../stores/useInterfaceStore';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

const API_BASE_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : '/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

// Interceptor to add the auth token to every request
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      // Ensure headers are defined
      if (!config.headers) {
        config.headers = {};
      }
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Empresa ativa selecionada (pode ser diferente da empresa do próprio usuário — ex.:
    // Consultoria navegando numa empresa-cliente) — o backend usa isso (validando o
    // escopo) pra escolher o banco de dados certo, não só o empresa_id do usuário logado.
    const empresaId = useInterfaceStore.getState().empresaId;
    if (empresaId) {
      if (!config.headers) {
        config.headers = {};
      }
      config.headers['X-Empresa-Ativa'] = String(empresaId);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const handleApiError = (error: any, url: string | undefined): ApiResponse<any> => {
  // Check if it's an axios error by checking for a specific property
  if (error.isAxiosError) {
    const { response, message } = error;
    console.error(`[API Request] ERRO na chamada para: ${url}`, response?.data || message);
    
    const status = response?.status;
    // Redireciona para login apenas em chamadas autenticadas — nunca nos próprios endpoints de login
    if (status === 401 && url !== '/login' && url !== '/login/google') {
      localStorage.removeItem('authToken');
      localStorage.removeItem('usuarioData');
      localStorage.removeItem('fourhands-session');
      window.location.href = '/login';
    }
    if (status === 402) {
      window.dispatchEvent(new CustomEvent('plan:limit-reached', { detail: response?.data }));
    }

    return { success: false, error: message, data: response?.data };
  }
  
  const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
  console.error(`[API Request] ERRO não-axios para: ${url}`, error);
  return { success: false, error: errorMessage };
};

const makeRequest = async <T>(config: any): Promise<ApiResponse<T>> => {
  try {
    const response = await apiClient.request<T>(config);
    return { success: true, data: response.data };
  } catch (error) {
    return handleApiError(error, config.url);
  }
};

export const apiMethods = {
  get: <T = any>(endpoint: string) => makeRequest<T>({ url: endpoint, method: 'GET' }),
  post: <T = any>(endpoint: string, data?: any) => makeRequest<T>({ url: endpoint, method: 'POST', data }),
  put: <T = any>(endpoint: string, data?: any) => makeRequest<T>({ url: endpoint, method: 'PUT', data }),
  delete: <T = any>(endpoint: string) => makeRequest<T>({ url: endpoint, method: 'DELETE' }),
  postForm: <T = any>(endpoint: string, data: FormData) =>
    makeRequest<T>({ url: endpoint, method: 'POST', data, headers: { 'Content-Type': 'multipart/form-data' } }),
  putForm: <T = any>(endpoint: string, data: FormData) =>
    makeRequest<T>({ url: endpoint, method: 'PUT', data, headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const api = {
  login: (email: string, password: string) =>
    apiMethods.post('/login', { email, password }),
  loginGoogle: (credential: string) =>
    apiMethods.post('/login/google', { credential }),
  getUsers: (empresa_id?: number) =>
    apiMethods.get(`/usuarios${empresa_id ? `?empresa_id=${empresa_id}` : ''}`),
  getUser: (id: number) => apiMethods.get(`/usuarios/${id}`),
  createUser: (userData: any) => apiMethods.post('/usuarios', userData),
  updateUser: (id: number, userData: any) => apiMethods.put(`/usuarios/${id}`, userData),
  deleteUser: (id: number) => apiMethods.delete(`/usuarios/${id}`),
  createCompany: (companyData: any) =>
    apiMethods.post('/empresas', companyData),
  getCompanies: (empresaId?: number) =>
    apiMethods.get(`/empresas${empresaId ? `?empresa_id=${empresaId}` : ''}`),
  getCompany: (id: number) => apiMethods.get(`/empresas/${id}`),
  updateCompany: (id: number, companyData: any) => apiMethods.put(`/empresas/${id}`, companyData),
  deleteCompany: (id: number) => apiMethods.delete(`/empresas/${id}`),
  deactivateCompany: (id: number, data: any) => apiMethods.put(`/empresas/${id}/desativar`, data),
  getQuotas: (empresaId: number) => apiMethods.get(`/quotas/${empresaId}`),
  createQuota: (quotaData: any) => apiMethods.post('/quotas', quotaData),
  deleteQuota: (id: number) => apiMethods.delete(`/quotas/${id}`),
  getFrameworks: () => apiMethods.get('/frameworks'),
  getFramework: (id: number) => apiMethods.get(`/frameworks/${id}`),
  getFrameworkDominios: (frameworkId: number) => apiMethods.get(`/frameworks/${frameworkId}/dominios`),
  createFramework: (frameworkData: any) =>
    apiMethods.post('/frameworks', frameworkData),
  updateFramework: (id: number, frameworkData: any) =>
    apiMethods.put(`/frameworks/${id}`, frameworkData),
  deleteFramework: (id: number) =>
    apiMethods.delete(`/frameworks/${id}`),
  associarQuestionario: (frameworkId: number, questionarioId: number) =>
    apiMethods.post(`/frameworks/${frameworkId}/questionarios`, { questionarioId }),
  desassociarQuestionario: (frameworkId: number, questionarioId: number) =>
    apiMethods.delete(`/frameworks/${frameworkId}/questionarios/${questionarioId}`),
  getQuestionariosFramework: (frameworkId: number) => apiMethods.get(`/frameworks/${frameworkId}/questionarios`),
  getQuestionariosDisponiveis: (frameworkId: number) => apiMethods.get(`/frameworks/${frameworkId}/questionarios/disponiveis`),
  validarAssociacao: (frameworkId: number, questionarioId: number) => apiMethods.get(`/frameworks/${frameworkId}/questionarios/${questionarioId}/validacao`),
  getQuestionnaires: () => apiMethods.get('/questionarios'),
  getQuestionnaire: (id: number) => apiMethods.get(`/questionarios/${id}`),
  createQuestionnaire: (questionnaireData: any) =>
    apiMethods.post('/questionarios', questionnaireData),
  updateQuestionnaire: (id: number, questionnaireData: any) =>
    apiMethods.put(`/questionarios/${id}`, questionnaireData),
  cloneQuestionnaire: (id: number, cloneData: any) =>
    apiMethods.post(`/questionarios/${id}/clonar`, cloneData),
  getPerguntasByFramework: (frameworkId: number) =>
    apiMethods.get(`/perguntas?framework_id=${frameworkId}`),
  getDominiosByFramework: (frameworkId: number) =>
    apiMethods.get(`/dominios?framework_id=${frameworkId}`),
  getQuestion: (id: number) => apiMethods.get(`/perguntas/${id}`),
  getQuestions: () => apiMethods.get('/perguntas'),
  getQuestionnaireQuestions: (id: number) => apiMethods.get(`/questionarios/${id}/perguntas`),
  createQuestion: (questionData: any) =>
    apiMethods.post('/perguntas', questionData),
  updateQuestion: (id: number, questionData: any) =>
    apiMethods.put(`/perguntas/${id}`, questionData),
  deleteQuestion: (id: number) =>
    apiMethods.delete(`/perguntas/${id}`),
  // Upload/URL do arquivo anexo de um documento da pergunta (bucket R2)
  uploadPerguntaDocumento: (file: File) => {
    const formData = new FormData();
    formData.append('arquivo', file);
    return apiMethods.postForm<{ arquivo_key: string; arquivo_nome: string }>('/perguntas/documento-upload', formData);
  },
  getPerguntaDocumentoUrl: (key: string) =>
    apiMethods.get<{ url: string }>(`/perguntas/documento-url?key=${encodeURIComponent(key)}`),
  createEquivalence: (equivalenceData: any) =>
    apiMethods.post('/perguntas/equivalencia', equivalenceData),
  getDomains: (empresa_id?: number) =>
    apiMethods.get(`/dominios${empresa_id ? `?empresa_id=${empresa_id}` : ''}`),
  createDomain: (domainData: any) =>
    apiMethods.post('/dominios', domainData),
  updateDomain: (id: number, domainData: any) =>
    apiMethods.put(`/dominios/${id}`, domainData),
  deleteDomain: (id: number) =>
    apiMethods.delete(`/dominios/${id}`),
  saveAnswer: (answerData: any) =>
    apiMethods.post('/respostas', answerData),
  sugerirPlanoDeAcao: (data: { pergunta_texto: string; dominio_nome?: string; framework_nome?: string; controle_indice?: string }) =>
    apiMethods.post('/ai/sugerir-plano-acao', data),
  // usuarioId ausente = modo Auditoria: traz as respostas de todos os usuários
  // que responderam (o auditor não é quem respondeu).
  getAnswers: (questionarioId: number, usuarioId?: number) =>
    apiMethods.get(`/questionarios/${questionarioId}/respostas${usuarioId ? `?usuario_id=${usuarioId}` : ''}`),
  salvarAuditoria: (data: {
    questionario_id: number; questionario_pergunta_id: number;
    classificacao_conformidade_id: number | null; comentario_auditor: string | null;
  }) => apiMethods.put('/respostas/auditoria', data),
  uploadAuditoriaAnexo: (data: { questionario_id: number; questionario_pergunta_id: number }, files: File[]) => {
    const formData = new FormData();
    files.forEach(f => formData.append('anexos', f));
    formData.append('questionario_id', String(data.questionario_id));
    formData.append('questionario_pergunta_id', String(data.questionario_pergunta_id));
    return apiMethods.postForm('/respostas/auditoria/anexo', formData);
  },
  getAuditoriaAnexoUrl: (id: number) =>
    apiMethods.get(`/respostas/auditoria/anexo/${id}`),
  deleteAuditoriaAnexo: (id: number) =>
    apiMethods.delete(`/respostas/auditoria/anexo/${id}`),
  saveChecklistItemResposta: (data: {
    questionario_id: number; pergunta_id: number; usuario_id: number; item_id: number;
    resposta: 'SIM' | 'NAO' | 'NA';
    justificativa?: string | null;
    plano_data_limite?: string | null;
    plano_responsavel_nome?: string | null;
    plano_responsavel_email?: string | null;
    plano_descricao?: string | null;
    evidencia_comentario?: string | null;
  }) => apiMethods.put('/respostas/checklist', data),
  uploadChecklistEvidencia: (data: { questionario_id: number; pergunta_id: number; usuario_id: number; item_id: number }, file: File) => {
    const formData = new FormData();
    formData.append('evidencia', file);
    formData.append('questionario_id', String(data.questionario_id));
    formData.append('pergunta_id', String(data.pergunta_id));
    formData.append('usuario_id', String(data.usuario_id));
    formData.append('item_id', String(data.item_id));
    return apiMethods.postForm('/respostas/checklist/evidencia', formData);
  },
  // usuario_id ausente = modo Auditoria (o auditor não é quem respondeu).
  getChecklistEvidenciaUrl: (data: { questionario_id: number; pergunta_id: number; usuario_id?: number; item_id: number }) =>
    apiMethods.get<{ url: string }>(`/respostas/checklist/evidencia?questionario_id=${data.questionario_id}&pergunta_id=${data.pergunta_id}&item_id=${data.item_id}${data.usuario_id ? `&usuario_id=${data.usuario_id}` : ''}`),
  // Evidência da PERGUNTA (Nível de Controle Mandatório/Recomendado + resposta SIM)
  uploadRespostaEvidencia: (data: { questionario_id: number; pergunta_id: number; usuario_id: number }, file: File) => {
    const formData = new FormData();
    formData.append('evidencia', file);
    formData.append('questionario_id', String(data.questionario_id));
    formData.append('pergunta_id', String(data.pergunta_id));
    formData.append('usuario_id', String(data.usuario_id));
    return apiMethods.postForm('/respostas/evidencia', formData);
  },
  // usuario_id ausente = modo Auditoria (o auditor não é quem respondeu).
  getRespostaEvidenciaUrl: (data: { questionario_id: number; pergunta_id: number; usuario_id?: number }) =>
    apiMethods.get<{ url: string }>(`/respostas/evidencia?questionario_id=${data.questionario_id}&pergunta_id=${data.pergunta_id}${data.usuario_id ? `&usuario_id=${data.usuario_id}` : ''}`),
  getEquivalencias: () => apiMethods.get('/perguntas/equivalencias'),
  createEquivalencia: (equivalenciaData: any) =>
    apiMethods.post('/perguntas/equivalencia', equivalenciaData),
  deleteEquivalencia: (id: number) =>
    apiMethods.delete(`/perguntas/equivalencias/${id}`),
  getEquivalenciasByPergunta: (perguntaId: number) =>
    apiMethods.get(`/perguntas/${perguntaId}/equivalencias`),
  aplicarRespostasEquivalentes: (respostaData: any) =>
    apiMethods.post('/respostas-equivalentes', respostaData),
  getRespostas: (perguntaId: number, questionarioId: number) =>
    apiMethods.get(`/respostas?perguntaId=${perguntaId}&questionarioId=${questionarioId}`),
  deleteQuestionnaire: (id: number) =>
    apiMethods.delete(`/questionarios/${id}`),
  updateQuestionnaireStatus: (id: number, status: 'RASCUNHO' | 'PUBLICADO' | 'ARQUIVADO') =>
    apiMethods.put(`/questionarios/${id}/status`, { status }),
  clearQuestionnaireAnswers: (id: number) =>
    apiMethods.delete(`/questionarios/${id}/respostas`),
  closeQuestionnaire: (id: number) =>
    apiMethods.put(`/questionarios/${id}/fechar`, {}),
  inativarQuestionnaire: (id: number) =>
    apiMethods.put(`/questionarios/${id}/inativar`, {}),
  verificarExclusaoQuestionnaire: (id: number) =>
    apiMethods.get(`/questionarios/${id}/verificar-exclusao`),
  getChecklistCompleto: (id: number) =>
    apiMethods.get(`/checklists/${id}/completo`),
  getFrameworksComDominios: () => apiMethods.get('/frameworks'),
  updateDatabase: (script: string) =>
    apiMethods.post('/admin/update-database', { script }),
  getContratos: () => apiMethods.get('/contratos'),
  getContrato: (id: number) => apiMethods.get(`/contratos/${id}`),
  createContrato: (formData: FormData) => apiMethods.postForm('/contratos', formData),
  updateContrato: (id: number, formData: FormData) => apiMethods.putForm(`/contratos/${id}`, formData),
  deleteContrato: (id: number) => apiMethods.delete(`/contratos/${id}`),
  getContratoDownloadUrl: (id: number, field: 'contrato_arquivo' | 'nda_arquivo') =>
    apiMethods.get<{ url: string }>(`/contratos/${id}/file/${field}`),
  getContratoEscopos: (contratoId: number) => apiMethods.get(`/contratos/${contratoId}/escopos`),
  createContratoEscopo: (contratoId: number, data: { escopo: string; representante_nome: string; representante_email: string }) =>
    apiMethods.post(`/contratos/${contratoId}/escopos`, data),
  updateContratoEscopo: (escopoId: number, data: { escopo?: string; representante_nome?: string; representante_email?: string }) =>
    apiMethods.put(`/contratos/escopos/${escopoId}`, data),
  deleteContratoEscopo: (escopoId: number) => apiMethods.delete(`/contratos/escopos/${escopoId}`),
  enviarQuestionarioEscopo: (escopoId: number, data: { questionario_id: number; data_alvo_resposta?: string }) =>
    apiMethods.post(`/contratos/escopos/${escopoId}/enviar`, data),
  getFornecedoresEscopoQuestionario: (questionarioId: number) =>
    apiMethods.get(`/questionarios/${questionarioId}/fornecedores-escopo`),
  revogarQuestionarioEscopo: (escopoId: number) => apiMethods.post(`/contratos/escopos/${escopoId}/revogar`, {}),
  getAcessoExternoLog: (acessoId: number) => apiMethods.get(`/acessos-externos/${acessoId}/log`),
  getDepartamentos: (empresa_id?: number, tipo?: string) => {
    const params = new URLSearchParams();
    if (empresa_id) params.append('empresa_id', String(empresa_id));
    if (tipo) params.append('tipo', tipo);
    return apiMethods.get(`/departamentos?${params.toString()}`);
  },
  getPessoas: (empresa_id?: number) =>
    apiMethods.get(`/pessoas${empresa_id ? `?empresa_id=${empresa_id}` : ''}`),
  getPessoa: (id: number) => apiMethods.get(`/pessoas/${id}`),
  createPessoa: (data: any) => apiMethods.post('/pessoas', data),
  updatePessoa: (id: number, data: any) => apiMethods.put(`/pessoas/${id}`, data),
  deletePessoa: (id: number) => apiMethods.delete(`/pessoas/${id}`),
  getDashboardMaturidade: (empresaId?: number) =>
    apiMethods.get(`/dashboard/maturidade${empresaId ? `?empresa_id=${empresaId}` : ''}`),
  getPerfisAcesso: () => apiMethods.get('/perfis-acesso'),
  getPerfilAcesso: (nome: string) => apiMethods.get(`/perfis-acesso/${encodeURIComponent(nome)}`),
  updatePerfilAcesso: (nome: string, data: { descricao: string }) =>
    apiMethods.put(`/perfis-acesso/${encodeURIComponent(nome)}`, data),
  updatePermissao: (nome: string, modulo: string, data: Record<string, boolean>) =>
    apiMethods.put(`/perfis-acesso/${encodeURIComponent(nome)}/permissoes/${encodeURIComponent(modulo)}`, data),
  getEstruturasOrganizacionais: (empresa_id?: number) =>
    apiMethods.get(`/estruturas-organizacionais${empresa_id ? `?empresa_id=${empresa_id}` : ''}`),
  getEstruturaOrganizacional: (id: number) =>
    apiMethods.get(`/estruturas-organizacionais/${id}`),
  createEstruturaOrganizacional: (data: any) =>
    apiMethods.post('/estruturas-organizacionais', data),
  updateEstruturaOrganizacional: (id: number, data: any) =>
    apiMethods.put(`/estruturas-organizacionais/${id}`, data),
  deleteEstruturaOrganizacional: (id: number) =>
    apiMethods.delete(`/estruturas-organizacionais/${id}`),
  getPlanos: () => apiMethods.get('/planos'),
  getAssinaturaAtiva: (empresaId: number) => apiMethods.get(`/assinaturas/${empresaId}/ativa`),
  getAssinaturas: (empresaId: number) => apiMethods.get(`/assinaturas/${empresaId}`),
  createAssinatura: (data: { empresa_id: number; plano_id: number; status?: string }) =>
    apiMethods.post('/assinaturas', data),
  cancelarAssinatura: (id: number) => apiMethods.put(`/assinaturas/${id}/cancelar`, {}),

  // Cadastros Auxiliares (tipo_empresa, ramo_atuacao, regulamentacao_setorial,
  // tipo_endereco, diretoria, gerencia, departamento, tipo_pessoa, escopo_questionario)
  getCadastrosAuxiliares: (tipo: string, empresaId?: number) =>
    apiMethods.get(`/cadastros-auxiliares?tipo=${encodeURIComponent(tipo)}${empresaId ? `&empresa_id=${empresaId}` : ''}`),
  createCadastroAuxiliar: (data: { tipo: string; nome: string; empresa_id?: number; nivel?: number | null; descricao?: string | null }) =>
    apiMethods.post('/cadastros-auxiliares', data),
  updateCadastroAuxiliar: (id: number, data: { nome?: string; nivel?: number | null; ativo?: boolean; descricao?: string | null }) =>
    apiMethods.put(`/cadastros-auxiliares/${id}`, data),
  deleteCadastroAuxiliar: (id: number) => apiMethods.delete(`/cadastros-auxiliares/${id}`),
  uploadCadastroAuxiliarDocumento: (id: number, file: File) => {
    const formData = new FormData();
    formData.append('documento', file);
    return apiMethods.postForm(`/cadastros-auxiliares/${id}/documento`, formData);
  },
  getCadastroAuxiliarDocumentoUrl: (id: number) =>
    apiMethods.get<{ url: string }>(`/cadastros-auxiliares/${id}/documento`),
};

export default api;